import { ChangeDetectionStrategy, Component, DestroyRef, HostListener, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { AutofocusDirective } from '../../directives/sia-autofocus.directive';
import { CrudDialogData } from '../../interfaces/crud-dialog-data';
import { SiaTranslatePipe } from '../../pipes/sia-translate.pipe';

@Component({
  selector: 'dialog-overview-example-dialog',
  templateUrl: `crud-dialog.component.html`,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AutofocusDirective,
    SiaTranslatePipe,
    MatDialogContent,
    MatDialogActions,
    MatDialogTitle,
    MatIconButton,
    MatButton,
    MatIcon,
    MatFormField,
    MatLabel,
    FormsModule,
    MatError,
    MatInput,
    MatSuffix,
  ],
})
export class CrudDialogComponent {
  readonly #destroyRef = inject(DestroyRef);
  readonly #dialogData = inject<CrudDialogData | null>(MAT_DIALOG_DATA, { optional: true }) ?? {};

  readonly dialogRef = inject<MatDialogRef<CrudDialogComponent, CrudDialogData | {}>>(MatDialogRef);
  readonly value = signal(this.#dialogData.value ?? '');
  readonly pageDataSet = signal(false);
  readonly showPassword = signal<boolean>(false);
  readonly isPassword = computed(() => this.#dialogData.isPassword ?? false);
  readonly inputType = computed(() => (this.isPassword() ? (this.showPassword() ? 'text' : 'password') : 'text'));
  readonly messageHtml = computed(() => this.#dialogData.title ?? '');
  readonly label = computed(() => this.#dialogData.label);
  readonly errorMessage = computed(() => this.#dialogData.errorMessage);
  readonly yesLabel = computed(() => this.#dialogData.yesLabel);
  readonly noLabel = computed(() => this.#dialogData.noLabel);
  readonly isValueInvalid = computed(
    () =>
      this.value().trim().length === 0 ||
      (this.#dialogData.regexPattern ? !new RegExp(this.#dialogData.regexPattern).test(this.value()) : false),
  );
  readonly showValidationError = computed(() => this.value().length > 0 && this.isValueInvalid());

  constructor() {
    this.dialogRef.updateSize('min(95% , 600px)');
  }

  onValueChange(value: string) {
    this.value.set(value);
  }

  togglePasswordVisibility() {
    this.showPassword.update((v) => !v);
  }

  onNoClick() {
    this.dialogRef.close(undefined);
  }

  onYesClick() {
    if (this.isValueInvalid()) {
      return;
    }

    this.dialogRef.close({
      value: this.value(),
    });
  }

  onEnterKey(event: Event) {
    event.preventDefault();
    this.onYesClick();
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (this.#dialogData.disableKeyboardShortcut) {
      return;
    }

    const target = event.target;
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement ||
      (target instanceof HTMLElement && target.isContentEditable)
    ) {
      return;
    }

    if (event.key === 'S' || event.key === 's') {
      this.onYesClick();
    } else if (event.key === 'N' || event.key === 'n') {
      this.onNoClick();
    }
  }
}
